"use client"

import Button from "components/Buttons/Button"
import Kicker from "components/Kicker"
import Avatar2 from "images/about/hero/Avatar2.png"
import Avatar1 from "images/brokers/cta/Avatar1.png"
import Avatar3 from "images/brokers/cta/Avatar3.png"
import LineIMG from "images/brokers/cta/Line.png"
import LineMobileImg from "images/brokers/cta/LineMobile.png"
import ShadingIMG from "images/brokers/cta/Shading.png"
import ShadingMobileIMG from "images/brokers/cta/ShadingMobile.png"
import StaticImage from "library/StaticImage"
import { css, f, styled } from "library/styled"
import { useMedia } from "library/useMedia"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

export default function CTA({ blog = false }: { blog?: boolean }) {
	const avatarSize = useMedia(90, 90, 100, 48)
	const buttonText = useMedia(
		"Get a Custom Quote Today",
		"Get a Custom Quote Today",
		"Get a Custom Quote Today",
		"Get a Custom Quote",
	)
	const avatars = [
		{ src: Avatar1, alt: "Avatar 1", id: 1 },
		{ src: Avatar2, alt: "Avatar 2", id: 2 },
		{ src: Avatar3, alt: "Avatar 3", id: 3 },
	]

	return (
		<Wrapper blog={blog}>
			<Outer>
				<Inner>
					<Line src={LineIMG} width={1286} height={545} alt="" />
					<Shading src={ShadingIMG} width={1286} height={680} alt="" />
					<LineMobile src={LineMobileImg} width={287} height={153} alt="" />
					<ShadingMobile
						src={ShadingMobileIMG}
						width={287}
						height={220}
						alt=""
					/>
					<KickerWrapper>
						<StyledKicker size="large">We’re ready to have you</StyledKicker>
					</KickerWrapper>
					<Title>Ready to get started?</Title>
					<Text>
						Join hundreds of businesses who trust Arlo to offer health insurance
						that works the way it should.
					</Text>
					<Button href={links.contact} variant="dark">
						{buttonText}
					</Button>
					<Avatars>
						{avatars.map((avatar) => (
							<Avatar
								key={avatar.id}
								src={avatar.src}
								alt={avatar.alt}
								width={avatarSize}
								height={avatarSize}
							/>
						))}
					</Avatars>
				</Inner>
			</Outer>
		</Wrapper>
	)
}

const Wrapper = styled("section", ({ blog }: { blog?: boolean }) => ({
	...f.responsive(css`
		display: grid;
		place-items: center;
		width: 100%;
		background: ${colors.green100Secondary};
		padding: 132px 0;
		border-radius: ${blog ? "20px 20px 0 0" : "0"};
	`),
	...f.mobile(css`
		padding: 110px 0;
	`),
}))

const Outer = styled("div", {
	...f.unresponsive(css`
		border: 1px solid ${colors.green300};
	`),
	...f.responsive(css`
		width: 1312px;
		padding: 12px;
		border-radius: 26px;
		margin: 0 auto;
	`),
	...f.tablet(css`
		width: 960px;
	`),
	...f.mobile(css`
		width: 327px;
		padding: 20px;
	`),
})

const Inner = styled("div", {
	...f.unresponsive(css`
		border: 1px solid ${colors.greenAccent};
	`),
	...f.responsive(css`
		display: grid;
		grid-template-rows: 40px 102px 118px 50px 140px;
		padding: 64px;
		border-radius: 16px;
		background: ${colors.greenMain};
		position: relative;
		overflow: clip;
	`),
	...f.tablet(css`
		grid-template-rows: 40px 108px 133px 50px 451px;
		padding: 120px 60px 60px;
	`),
	...f.mobile(css`
		grid-template-rows: repeat(4, auto) 144px;
		gap: 32px;
		padding: 32px 20px 20px;
	`),
})

const Image = styled(StaticImage, {
	...f.responsive(css`
		position: absolute;
		top: 0;
		left: 0;
		max-inline-size: unset !important;
		max-block-size: unset !important;
		width: 1286px;
	`),
	...f.tablet(css`
		top: unset;
		left: -52px;
	`),
	...f.mobile(css`
		top: unset;
		width: 287px;
	`),
})

const Line = styled(Image, {
	...f.responsive(css`
		height: 545px;
	`),
	...f.tablet(css`
		bottom: 118px;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const LineMobile = styled(Image, {
	...f.responsive(css`
		display: none;
		height: 153px;
	`),
	...f.mobile(css`
		display: block;
		bottom: 25px;
	`),
})

const Shading = styled(Image, {
	...f.responsive(css`
		height: 680px;
	`),
	...f.tablet(css`
		bottom: -17px;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const ShadingMobile = styled(Image, {
	...f.responsive(css`
		display: none;
		height: 220px;
	`),
	...f.mobile(css`
		display: block;
		bottom: -42px;
	`),
})

const KickerWrapper = styled("div", {
	...f.responsive(css`
		display: flex;
		align-items: flex-start;
		margin-left: -7px;
	`),
})

const StyledKicker = styled(Kicker, {
	...f.responsive(css`
		color: ${colors.greenAccent};
	`),
})

const Title = styled("h3", {
	...f.responsive(css`
		${textStyles.h4};
		color: ${colors.white};
	`),
	...f.tablet(css`
		${textStyles.h3};
	`),
	...f.mobile(css`
		${textStyles.h6};
	`),
})

const Text = styled("p", {
	...f.responsive(css`
		${textStyles.bodyR};
		color: ${colors.green100};
		width: 354px;
	`),
	...f.tablet(css`
		${textStyles.bodyL};
		width: 408px;
	`),
	...f.mobile(css`
		${textStyles.bodyS};
		width: 227px;
	`),
})

const Avatars = styled("div", {
	...f.responsive(css`
		display: flex;
		justify-content: flex-end;
		align-items: flex-end;
	`),
})

const Avatar = styled(StaticImage, {
	...f.responsive(css`
		width: 90px;
		height: 90px;
		border-radius: 50%;

		&:nth-of-type(1) {
			transform: translateX(38px);
			z-index: 2;
		}

		&:nth-of-type(2) {
			transform: translateX(20px);
			z-index: 1;
		}

		&:nth-of-type(3) {
			transform: translateX(0);
			z-index: 0;
		}
	`),
	...f.tablet(css`
		width: 100px;
		height: 100px;

		&:nth-of-type(1) {
			transform: translateX(50px);
		}

		&:nth-of-type(2) {
			transform: translateX(26px);
		}
	`),
	...f.mobile(css`
		width: 48px;
		height: 48px;

		&:nth-of-type(1) {
			transform: translateX(24px);
		}

		&:nth-of-type(2) {
			transform: translateX(12px);
		}
	`),
})
